
import { Component, Input, Output, EventEmitter, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { UiStateService } from '../shared/ui-state.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { CommentsNotificationsComponent } from '../comments/comments-notifications.component';
import { CommentsComponent } from '../comments/comments.component';
import { HistoryComponent } from '../history/history.component';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, CommentsNotificationsComponent, CommentsComponent, HistoryComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() set notificationsCount(value: number) {
    console.log('🔔 Header received notificationsCount:', value);
    this._notificationsCount = value;
  }
  get notificationsCount(): number {
    return this._notificationsCount;
  }
  private _notificationsCount: number = 0;
  
  @Input() showSearchIcon: boolean = false;
  @Input() showFilterIcon: boolean = false;
  @Input() showDesktopFilters: boolean = false;

  searchSelected: boolean = false;
  filterSelected: boolean = false;
  openId: string = '';
  dropdownSearch: string = '';

  // Desktop navigation state
  activeTab: string = 'home';
  
  // Desktop comments panel state
  showCommentsPanel: boolean = false;
  showCommentsNotificationsPanel: boolean = false;
  
  // Comments panel data
  commentsPassportId: string | null = null;
  commentsHeader: any = null;
  commentsReadOnly = false;

  // Desktop history panel state
  showHistoryPanel: boolean = false;
  
  // History panel data
  historyPassportId: string | null = null;
  historyHeader: any = null;

  private readonly subscriptions: Subscription[] = [];

  private getVisibleOptions(options: string[]): string[] {
    const q = (this.dropdownSearch || '').toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }

  isAllVisibleSelected(def: { id: string; label: string; options: string[] }): boolean {
    const visible = this.getVisibleOptions(def.options);
    const selected = this.filterService.filters()[def.id] || [];
    return visible.length > 0 && visible.every(v => selected.includes(v));
  }

  onToggleSelectAll(def: { id: string; label: string; options: string[] }): void {
    const visible = this.getVisibleOptions(def.options);
    const selected = this.filterService.filters()[def.id] || [];
    const allSelected = visible.length > 0 && visible.every(v => selected.includes(v));
    if (allSelected) {
      const next = selected.filter(v => !visible.includes(v));
      this.filterService.setFilter(def.id, next);
    } else {
      const set = new Set<string>([...selected, ...visible]);
      this.filterService.setFilter(def.id, Array.from(set));
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    // Dismiss any open dropdown when clicking outside
    if (this.openId) {
      this.openId = '';
      this.dropdownSearch = '';
    }
    if (!this.searchSelected && !this.filterSelected) {
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
    }
  }

  // Filter API
  @Input() filterDefinitions: Array<{ id: string; label: string; options: string[] }> = [];

  @Output() commentsClick = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  constructor(
    public searchService: SearchService, 
    public filterService: FilterService, 
    public ui: UiStateService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly personaRouting: PersonaRoutingService,
    private readonly commentsPanelService: CommentsPanelService,
    private readonly historyPanelService: HistoryPanelService
  ) {}

  ngOnInit(): void {
    // Set initial active tab based on current route
    this.updateActiveTabFromRoute(this.router.url);

    // Listen to route changes to update active tab
    const routeSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateActiveTabFromRoute(event.urlAfterRedirects || event.url);
      });
    this.subscriptions.push(routeSub);

    // Listen to comments panel requests
    const commentsSub = this.commentsPanelService.openComments$.subscribe(request => {
      this.openCommentsForPassport(request.passportId, request.header, request.readOnly);
    });
    this.subscriptions.push(commentsSub);

    // Listen to history panel requests
    const historySub = this.historyPanelService.openHistory$.subscribe(request => {
      this.openHistoryForPassport(request.passportId, request.header);
    });
    this.subscriptions.push(historySub);
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  private updateActiveTabFromRoute(url: string): void {
    // Store the active tab in sessionStorage for persistence
    const storedTab = sessionStorage.getItem('activeTab');
    
    if (url.includes('/profile')) {
      this.activeTab = 'profile';
      sessionStorage.setItem('activeTab', 'profile');
    } else if (url.includes('/contacts')) {
      this.activeTab = 'contacts';
      sessionStorage.setItem('activeTab', 'contacts');
    } else if (url.includes('/comments')) {
      // Don't change active tab when viewing comments
      this.activeTab = storedTab || 'home';
    } else if (url === '/login' || url === '/sso') {
      // Don't set active tab on login pages
      this.activeTab = '';
    } else {
      // For all other routes (growers, gatehouse, transloading, scheduler, admin, etc.)
      this.activeTab = 'home';
      sessionStorage.setItem('activeTab', 'home');
    }
  }

  onDesktopNavClick(tab: string): void {
    this.activeTab = tab;
    sessionStorage.setItem('activeTab', tab);

    const persona = localStorage.getItem('persona') || '';
    
    switch (tab) {
      case 'home': {
        // Navigate to persona-specific home route
        const homeRoute = this.personaRouting.personaToRoute(persona);
        this.router.navigateByUrl(homeRoute);
        break;
      }
      case 'contacts':
        this.router.navigateByUrl('/contacts');
        break;
      case 'profile':
        this.router.navigateByUrl('/profile');
        break;
    }
  }

  onToggleSearch(): void {
    const next = !this.searchSelected;
    this.searchSelected = next;
    if (next) {
      this.filterSelected = false;
      // Clear filters when switching to search
      this.filterService.clear();
      this.openId = '';
      this.dropdownSearch = '';
    }
    this.ui.setSearchOpen(this.searchSelected);
    this.ui.setFiltersOpen(this.filterSelected);
  }

  onToggleFilter(): void {
    const next = !this.filterSelected;
    this.filterSelected = next;
    if (next) {
      this.searchSelected = false;
      // Clear search when switching to filters
      this.searchService.setQuery('');
    }
    this.ui.setSearchOpen(this.searchSelected);
    this.ui.setFiltersOpen(this.filterSelected);
  }

  onCommentsClick(): void {
    // Check if desktop view (window width >= 768px)
    const isDesktop = window.innerWidth >= 768;
    
    if (isDesktop) {
      // Toggle comments notifications panel for desktop
      this.showCommentsNotificationsPanel = !this.showCommentsNotificationsPanel;
      // Close comments panel if open
      if (this.showCommentsNotificationsPanel) {
        this.showCommentsPanel = false;
      }
    } else {
      // Navigate to comments notifications page for mobile
      void this.router.navigate(['/comments-notifications']);
    }
  }

  closeCommentsPanel(): void {
    this.showCommentsPanel = false;
  }

  closeCommentsNotificationsPanel(): void {
    this.showCommentsNotificationsPanel = false;
  }

  openCommentsForPassport(passportId: string, header: any, readOnly = false): void {
    const isDesktop = window.innerWidth >= 768;
    
    if (isDesktop) {
      // Open comments panel for desktop
      this.commentsPassportId = passportId;
      this.commentsHeader = header;
      this.commentsReadOnly = readOnly;
      this.showCommentsPanel = true;
      this.showCommentsNotificationsPanel = false;
      this.showHistoryPanel = false;
    } else {
      // Navigate to comments page for mobile
      void this.router.navigate(['/comments'], { state: { passportId, header, readOnly } });
    }
  }

  openHistoryForPassport(passportId: string, header: any): void {
    const isDesktop = window.innerWidth >= 768;
    
    if (isDesktop) {
      // Open history panel for desktop
      this.historyPassportId = passportId;
      this.historyHeader = header;
      this.showHistoryPanel = true;
      this.showCommentsPanel = false;
      this.showCommentsNotificationsPanel = false;
    } else {
      // Navigate to history page for mobile
      void this.router.navigate(['/history'], { state: { passportId, header } });
    }
  }

  closeHistoryPanel(): void {
    this.showHistoryPanel = false;
  }

  onSelectChange(id: string, value: string): void {
    this.filterService.setFilter(id, value);
  }

  onRefreshClick(): void {
    this.refresh.emit();
  }

  hasActiveFilters(): boolean {
    return this.filterDefinitions.some(def => {
      const selected = this.filterService.filters()[def.id];
      return (selected?.length ?? 0) > 0;
    });
  }

  onClearAllFilters(): void {
    this.filterService.clear();
    this.openId = '';
    this.dropdownSearch = '';
  }

  getFilterDisplayValue(filterId: string): string {
    const selected = this.filterService.filters()[filterId] || [];
    if (selected.length === 0) return 'Select';
    if (selected.length === 1) return selected[0];
    return selected.join(', ');
  }
}


